import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import TrainingModuleReader, { TYPE_META } from "@/components/TrainingModuleReader";
import {
  Plus,
  Trash2,
  Save,
  X,
  Eye,
  Tag,
  Clock,
  Megaphone,
  Stethoscope,
  Library,
  TrendingUp,
  Users2,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const STATUSES = ["draft", "published", "archived"];
const CONTENT_TYPES = ["video", "doc", "checklist", "deck", "pdf", "external"];
const AUDIENCES = [
  { id: "clinic", label: "Clinics", subtitle: "Learning & operating the platform", icon: Stethoscope },
  { id: "sales", label: "Selling & Marketing", subtitle: "Selling Nodera to clinics + vendors", icon: Megaphone },
];
const EMPTY = {
  audience: "clinic",
  track: "Getting started",
  title: "",
  description: "",
  content_type: "doc",
  content_url: "",
  body: "",
  duration_minutes: "",
  order_index: 1,
  status: "draft",
};

export default function AdminTraining() {
  const [view, setView] = useState("library"); // library | adoption
  const [audience, setAudience] = useState("clinic");
  const [items, setItems] = useState([]);
  const [tracks, setTracks] = useState({ clinic: [], sales: [] });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [reading, setReading] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/training-modules");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
    api.get("/training-tracks").then((r) => setTracks(r.data || {})).catch(() => {});
  }, []);

  const filtered = useMemo(() => items.filter((m) => m.audience === audience), [items, audience]);
  const grouped = useMemo(() => {
    const g = {};
    for (const m of filtered) {
      const k = m.track || "Untracked";
      if (!g[k]) g[k] = [];
      g[k].push(m);
    }
    return g;
  }, [filtered]);

  const counts = {
    clinic: items.filter((m) => m.audience === "clinic").length,
    sales: items.filter((m) => m.audience === "sales").length,
  };
  const publishedCount = filtered.filter((m) => m.status === "published").length;

  const startNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, audience });
  };
  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      ...EMPTY,
      ...m,
      content_url: m.content_url || "",
      body: m.body || "",
      description: m.description || "",
      duration_minutes: m.duration_minutes ?? "",
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title required.");
      return;
    }
    if (!form.track) {
      toast.error("Track required.");
      return;
    }
    const payload = {
      ...form,
      content_url: form.content_url || null,
      body: form.body || null,
      description: form.description || null,
      duration_minutes: form.duration_minutes === "" ? null : Number(form.duration_minutes),
      order_index: Number(form.order_index) || 0,
    };
    try {
      if (editing === "new") {
        await api.post("/training-modules", payload);
        toast.success("Module created.");
      } else {
        await api.patch(`/training-modules/${editing}`, payload);
        toast.success("Module updated.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePublish = async (m) => {
    const next = m.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/training-modules/${m.id}`, { status: next });
      toast.success(next === "published" ? "Deployed to clinics." : "Unpublished.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this training module?")) return;
    try {
      await api.delete(`/training-modules/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const audienceTracks = tracks[audience] || [];

  return (
    <ToolPageShell
      eyebrow="Admin · Training"
      title="Training modules"
      intro="Two libraries: Clinics (platform operations) + Selling & Marketing (how to sell Nodera to clinics and recruit vendors). Publish modules to deploy them — clinics see published modules in their portal."
      actions={
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="training-view-tabs">
            <button
              type="button"
              data-testid="training-view-library"
              onClick={() => setView("library")}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors inline-flex items-center gap-1 ${
                view === "library" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Library className="w-3 h-3" /> Library
            </button>
            <button
              type="button"
              data-testid="training-view-adoption"
              onClick={() => setView("adoption")}
              className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors inline-flex items-center gap-1 ${
                view === "adoption" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <TrendingUp className="w-3 h-3" /> Adoption
            </button>
          </div>
          {view === "library" && (
            <button
              data-testid="training-new-btn"
              onClick={startNew}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5" /> New module
            </button>
          )}
        </div>
      }
    >
      {view === "adoption" && <AdoptionView />}

      {view === "library" && (
        <>
      <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="training-audience-tabs">
        {AUDIENCES.map((a) => {
          const Icon = a.icon;
          const active = audience === a.id;
          return (
            <button
              key={a.id}
              type="button"
              data-testid={`training-audience-${a.id}`}
              onClick={() => setAudience(a.id)}
              className={`text-left rounded-2xl border p-4 transition-colors ${
                active
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${
                    active ? "bg-white/10 border-white/20" : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold">{a.label}</div>
                  <div className={`text-xs mt-0.5 ${active ? "text-blue-200" : "text-slate-500"}`}>
                    {a.subtitle}
                  </div>
                </div>
                <span
                  className={`text-xs font-bold tabular rounded-full px-2 py-0.5 ${
                    active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {counts[a.id]}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900 tabular">{filtered.length}</span> total ·{" "}
          <span className="font-semibold text-emerald-700 tabular">{publishedCount}</span> published
        </div>
      </div>

      {editing && (
        <div data-testid="training-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Audience" required>
              <select
                value={form.audience}
                onChange={(e) =>
                  setForm({ ...form, audience: e.target.value, track: (tracks[e.target.value] || [""])[0] })
                }
                className={inputCls}
                data-testid="training-form-audience"
              >
                <option value="clinic">Clinics</option>
                <option value="sales">Selling & Marketing</option>
              </select>
            </Field>
            <Field label="Track" required>
              <select
                value={form.track}
                onChange={(e) => setForm({ ...form, track: e.target.value })}
                className={inputCls}
                data-testid="training-form-track"
              >
                {(tracks[form.audience] || []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Title" required>
            <input
              data-testid="training-form-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Description" hint="Shown in the card preview before opening.">
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Content type">
              <select
                value={form.content_type}
                onChange={(e) => setForm({ ...form, content_type: e.target.value })}
                className={inputCls}
              >
                {CONTENT_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Duration (min)">
              <input
                type="number"
                min={0}
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Order">
              <input
                type="number"
                value={form.order_index}
                onChange={(e) => setForm({ ...form, order_index: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="External URL" hint="Optional — Loom, Notion, Google Doc, hosted video, etc.">
            <input
              value={form.content_url}
              onChange={(e) => setForm({ ...form, content_url: e.target.value })}
              placeholder="https://"
              className={inputCls}
            />
          </Field>
          <Field label="Body" hint="Plain text. Whitespace + bullets preserved on render.">
            <textarea
              rows={10}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="training-form-body"
              className={`${inputCls} resize-y font-mono text-xs`}
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className={inputCls}
              data-testid="training-form-status"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="training-form-save"
              onClick={save}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button
              onClick={cancel}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Modules grouped by track */}
      {audienceTracks.map((track) => {
        const list = (grouped[track] || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        if (list.length === 0) return null;
        return (
          <section key={track} className="mb-8" data-testid={`training-track-${track.replace(/\s+/g, "-").toLowerCase()}`}>
            <h2 className="font-display text-base font-bold text-slate-900 mb-3 inline-flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5 text-slate-400" /> {track}
              <span className="text-xs font-normal text-slate-400">· {list.length}</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((m) => (
                <ModuleCard
                  key={m.id}
                  module={m}
                  onView={() => setReading(m)}
                  onEdit={() => startEdit(m)}
                  onTogglePublish={() => togglePublish(m)}
                  onDelete={() => remove(m.id)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* Untracked */}
      {Object.keys(grouped)
        .filter((t) => !audienceTracks.includes(t))
        .map((track) => (
          <section key={track} className="mb-8">
            <h2 className="font-display text-base font-bold text-slate-900 mb-3">{track} (custom)</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[track].map((m) => (
                <ModuleCard
                  key={m.id}
                  module={m}
                  onView={() => setReading(m)}
                  onEdit={() => startEdit(m)}
                  onTogglePublish={() => togglePublish(m)}
                  onDelete={() => remove(m.id)}
                />
              ))}
            </div>
          </section>
        ))}

      {filtered.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          No modules in this audience yet. Click "New module" to add one.
        </div>
      )}
        </>
      )}

      {reading && <TrainingModuleReader module={reading} onClose={() => setReading(null)} />}
    </ToolPageShell>
  );
}

function ModuleCard({ module: m, onView, onEdit, onTogglePublish, onDelete }) {
  const meta = TYPE_META[m.content_type] || TYPE_META.doc;
  const Icon = meta.icon;
  return (
    <div data-testid={`training-card-${m.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
      <div className="flex items-start gap-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${meta.tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-slate-900 leading-snug">{m.title}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1.5">
            {meta.label}
            {m.duration_minutes ? (
              <>
                <span>·</span>
                <Clock className="w-3 h-3" /> {m.duration_minutes} min
              </>
            ) : null}
          </div>
        </div>
        <StatusPill status={m.status} />
      </div>
      {m.description && <p className="mt-3 text-xs text-slate-600 line-clamp-3 flex-1">{m.description}</p>}
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
        <button
          onClick={onView}
          data-testid={`training-view-${m.id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
        >
          <Eye className="w-3 h-3" /> View
        </button>
        <button onClick={onEdit} className="text-xs font-semibold text-slate-700 hover:text-slate-900">
          Edit
        </button>
        <button
          onClick={onTogglePublish}
          data-testid={`training-toggle-publish-${m.id}`}
          className={`text-xs font-semibold rounded-full px-3 py-1 border ${
            m.status === "published"
              ? "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
          }`}
        >
          {m.status === "published" ? "Unpublish" : "Publish & deploy"}
        </button>
        <button onClick={onDelete} className="inline-flex items-center text-xs text-red-600 hover:text-red-800 ml-auto">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function fmtAt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function AdoptionView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/training-adoption")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading adoption…</div>;
  if (!data) return null;

  const rows = (data.rows || []).filter((r) => {
    if (filter === "finished") return r.percent === 100;
    if (filter === "in-progress") return r.percent > 0 && r.percent < 100;
    if (filter === "stuck") return r.percent === 0;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="training-adoption">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Clinics" value={data.total_clinics} icon={Users2} />
        <Stat
          label="Library size"
          value={`${data.total_modules} ${data.total_modules === 1 ? "module" : "modules"}`}
          icon={Library}
        />
        <Stat label="Network adoption" value={`${data.global_percent}%`} icon={TrendingUp} />
        <Stat label="100% complete" value={data.finished_clinics} icon={Sparkles} tone="emerald" />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="adoption-filter">
        <FilterBtn id="all" label="All" active={filter === "all"} onClick={() => setFilter("all")} />
        <FilterBtn
          id="in-progress"
          label="In progress"
          active={filter === "in-progress"}
          onClick={() => setFilter("in-progress")}
        />
        <FilterBtn
          id="finished"
          label="Finished"
          active={filter === "finished"}
          onClick={() => setFilter("finished")}
        />
        <FilterBtn
          id="stuck"
          label="Not started"
          active={filter === "stuck"}
          onClick={() => setFilter("stuck")}
        />
        <span className="ml-auto text-xs text-slate-500">{rows.length} clinics</span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="adoption-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Clinic</th>
              <th className="text-left px-4 py-3">Progress</th>
              <th className="text-left px-4 py-3">Completed</th>
              <th className="text-left px-4 py-3">Last activity</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const isExp = expanded === r.clinic_user_id;
              const stuck = r.percent === 0;
              const done = r.percent === 100;
              return (
                <Fragment key={r.clinic_user_id}>
                  <tr data-testid={`adoption-row-${r.clinic_user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-semibold text-slate-900">{r.clinic_name}</div>
                      <div className="text-[11px] text-slate-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 w-[40%]">
                      <div className="flex items-center gap-3">
                        <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              done ? "bg-emerald-500" : stuck ? "bg-slate-300" : "bg-slate-900"
                            }`}
                            style={{ width: `${r.percent}%` }}
                          />
                        </div>
                        <span className={`text-xs font-bold tabular w-10 text-right ${done ? "text-emerald-700" : "text-slate-700"}`}>
                          {r.percent}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular text-slate-700">
                      {r.completed} / {r.total_modules}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtAt(r.last_completed_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setExpanded(isExp ? null : r.clinic_user_id)}
                        data-testid={`adoption-expand-${r.clinic_user_id}`}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                      >
                        {isExp ? "Hide" : "By track"} →
                      </button>
                    </td>
                  </tr>
                  {isExp && (
                    <tr data-testid={`adoption-tracks-${r.clinic_user_id}`}>
                      <td colSpan={5} className="bg-slate-50/60 px-4 py-4">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {r.by_track.map((t) => {
                            const pct = t.total ? Math.round((t.completed / t.total) * 100) : 0;
                            const trackDone = pct === 100;
                            return (
                              <div
                                key={t.track}
                                className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500 truncate">
                                    {t.track}
                                  </div>
                                  <div className="text-xs text-slate-700 tabular">
                                    {t.completed}/{t.total}
                                  </div>
                                </div>
                                {trackDone ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                ) : (
                                  <span className="text-xs font-bold tabular text-slate-500 shrink-0">{pct}%</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-slate-500 py-10">
                  No clinics match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-400">
        "Network adoption" = percent of (clinic × module) pairs completed across all clinics. Use the "Not started"
        filter to spot clinics worth a quick CS check-in.
      </p>
    </div>
  );
}

function FilterBtn({ id, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`adoption-filter-${id}`}
      onClick={onClick}
      className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const wrap =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-100 text-emerald-700"
      : "bg-slate-50 border-slate-200 text-slate-600";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
      <span className={`w-9 h-9 rounded-lg border flex items-center justify-center ${wrap}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 truncate">{label}</div>
        <div className="font-display text-lg font-bold text-slate-900 tabular truncate">{value}</div>
      </div>
    </div>
  );
}
